import * as THREE from 'three'

const FLOOR_HEIGHT = 3.0
const TUBE_RADIUS = 0.08
const ARROW_SIZE = 0.2
const ARROW_SPACING = 3

interface Position3D {
  x: number
  z: number
  floor: number
}

export class RouteRenderer {
  group: THREE.Group
  private tubeMesh: THREE.Mesh | null = null
  private arrows: THREE.Mesh[] = []

  constructor() {
    this.group = new THREE.Group()
    this.group.name = 'route'
  }

  showRoute(path: Position3D[]) {
    this.clearRoute()
    if (path.length < 2) return

    // Convert to 3D points
    const points = path.map(
      (p) => new THREE.Vector3(p.x, p.floor * FLOOR_HEIGHT + 0.5, p.z)
    )

    // Create tube
    const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal', 0.5)
    const geometry = new THREE.TubeGeometry(curve, points.length * 10, TUBE_RADIUS, 8, false)

    // Gradient material: green -> red
    const colors = new Float32Array(geometry.attributes.position!.count * 3)
    const posAttr = geometry.attributes.position!
    for (let i = 0; i < posAttr.count; i++) {
      const point = new THREE.Vector3().fromBufferAttribute(posAttr, i)
      // Find closest t on curve
      const t = this.estimateT(point, curve)
      // Green to red gradient
      const r = t
      const g = 1 - t
      const b = 0
      colors[i * 3] = r
      colors[i * 3 + 1] = g
      colors[i * 3 + 2] = b
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))

    const material = new THREE.MeshPhongMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
    })

    this.tubeMesh = new THREE.Mesh(geometry, material)
    this.group.add(this.tubeMesh)

    // Directional arrows
    const totalLength = curve.getLength()
    const arrowCount = Math.floor(totalLength / ARROW_SPACING)
    for (let i = 1; i <= arrowCount; i++) {
      const t = (i * ARROW_SPACING) / totalLength
      if (t >= 1) break
      const pos = curve.getPointAt(t)
      const tangent = curve.getTangentAt(t)

      const arrowGeom = new THREE.ConeGeometry(ARROW_SIZE, ARROW_SIZE * 2, 6)
      const arrowColor = new THREE.Color(t, 1 - t, 0)
      const arrowMat = new THREE.MeshPhongMaterial({ color: arrowColor })
      const arrow = new THREE.Mesh(arrowGeom, arrowMat)
      arrow.position.copy(pos)

      // Orient arrow along tangent
      const up = new THREE.Vector3(0, 1, 0)
      const quat = new THREE.Quaternion().setFromUnitVectors(up, tangent.normalize())
      arrow.quaternion.copy(quat)

      this.arrows.push(arrow)
      this.group.add(arrow)
    }
  }

  private estimateT(point: THREE.Vector3, curve: THREE.CatmullRomCurve3): number {
    let minDist = Infinity
    let bestT = 0
    const steps = 50
    for (let i = 0; i <= steps; i++) {
      const t = i / steps
      const p = curve.getPointAt(t)
      const d = point.distanceTo(p)
      if (d < minDist) {
        minDist = d
        bestT = t
      }
    }
    return bestT
  }

  clearRoute() {
    if (this.tubeMesh) {
      this.group.remove(this.tubeMesh)
      this.tubeMesh.geometry.dispose()
      ;(this.tubeMesh.material as THREE.Material).dispose()
      this.tubeMesh = null
    }
    for (const arrow of this.arrows) {
      this.group.remove(arrow)
      arrow.geometry.dispose()
      ;(arrow.material as THREE.Material).dispose()
    }
    this.arrows = []
  }

  dispose() {
    this.clearRoute()
  }
}
